import { createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { getFocusedRouteNameFromRoute } from '@react-navigation/native'
import { Badge, Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { ConversationsSection } from 'app/concept_data_handler'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useChatConfig } from 'chat/config/queries/get'
import { ConversationTabParamList } from 'chat/conversations/types'
import { Image, ImageSource } from 'expo-image'
import { ConversationTabProps } from 'home/stack/types'
import React, { useLayoutEffect, useMemo } from 'react'
import { Platform, View } from 'react-native'
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from 'react-native-safe-area-context'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { useConversationsSections } from './queries/context'
import { ConversationsScreen } from './screen'

const Tab = createBottomTabNavigator<ConversationTabParamList>()
const BOTTOM_TAB_HEIGHT = 70
interface ConverastionTabInfo {
  image: ImageSource
  label: string
  unreadCount: number
  title: string
}

export function ConversationsTab({ navigation, route }: ConversationTabProps) {
  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const styles = useStyles()

  const { data: user } = useUser()
  const { data: chatConfig } = useChatConfig()
  const conversationsSections = useConversationsSections()
  const defaultTab = mostUnreadSection(conversationsSections)
  const isChatEnabled = user?.chat?.enabled
  const { bottom } = useSafeAreaInsets()

  const initialRoute: keyof ConversationTabParamList = user?.permissions
    ?.showGroupedConversations
    ? defaultTab || 'dm'
    : 'allConversations'

  const tabDetails = useMemo(() => {
    const details: Record<keyof ConversationTabParamList, ConverastionTabInfo> =
      {
        announcements: {
          image: require('assets/chat/announcement.png'),
          label: t('announcements'),
          unreadCount:
            conversationsSections.find(
              section => section.type === 'announcements'
            )?.unread ?? 0,
          title: t('announcements'),
        },
        dm: {
          image: require('assets/chat/dm.png'),
          label: 'DMs',
          unreadCount:
            conversationsSections.find(section => section.type === 'direct')
              ?.unread ?? 0,
          title: t('dm'),
        },
        groups: {
          image: require('assets/chat/groupchat.png'),
          label: t('groups'),
          unreadCount:
            (conversationsSections.find(section => section.type === 'group')
              ?.unread ?? 0) +
            (conversationsSections.find(section => section.type === 'custom')
              ?.unread ?? 0),
          title: t('groups'),
        },
        marked: {
          image: require('assets/chat/marked.png'),
          label: t('marked'),
          unreadCount: 0,
          title: t('markedMessages'),
        },
        sent: {
          image: require('assets/chat/sent.png'),
          label: t('sent'),
          unreadCount: 0,
          title: t('sentMessages'),
        },
        allConversations: {
          image: require('assets/chat/dm.png'),
          label: t('allConversations'),
          unreadCount: 0,
          title: t('allConversations'),
        },
      }

    return details
  }, [conversationsSections])

  useLayoutEffect(() => {
    const focusedRouteName = getFocusedRouteNameFromRoute(route) ?? initialRoute
    const headerTitle = tabDetails[focusedRouteName]?.title

    showHeaderTitle(navigation, headerTitle ?? '')
    navigation.setOptions({ headerRight: () => null })
  }, [
    chatConfig?.allowSearchMessages,
    handleNavigation,
    initialRoute,
    isChatEnabled,
    navigation,
    route,
    tabDetails,
  ])

  return (
    <SafeAreaProvider>
      {isChatEnabled && chatConfig?.allowSearchMessages && (
        <PressableOpacity
          style={styles.searchBarContainer}
          onPress={() => {
            handleNavigation({ routeName: 'search_messages' })
          }}
        >
          <View style={styles.searchBarInputContainer}>
            <Icon
              name="search"
              type="feather"
              size={18}
              color={theme.colors.grey3}
            />
            <Text style={styles.searchBarText}>{t('search')}</Text>
          </View>
        </PressableOpacity>
      )}
      <Tab.Navigator
        initialRouteName={initialRoute}
        screenOptions={props => {
          const { image, label, unreadCount } = tabDetails[props.route.name]

          return {
            cardStyle: { backgroundColor: theme.colors?.white },
            headerShown: false,
            animation: 'shift',
            tabBarShowLabel: false,
            tabBarStyle: { height: BOTTOM_TAB_HEIGHT + bottom },
            tabBarButton: ({ children, onPress, ...rest }) => (
              <PressableOpacity
                style={styles.tabBarItemContainer}
                onPress={e => {
                  showHeaderTitle(
                    navigation,
                    tabDetails[props.route.name]?.title ?? ''
                  )
                  if (Platform.OS === 'web') {
                    e.preventDefault?.()
                  }
                  onPress?.(e)
                }}
                {...rest}
              >
                {children}
              </PressableOpacity>
            ),
            tabBarIcon: ({ focused }) => (
              <View style={styles.tabBarIconLabelContainer}>
                <View
                  style={[
                    styles.tab,
                    {
                      backgroundColor: focused
                        ? theme.colors?.grey5
                        : 'transparent',
                    },
                  ]}
                >
                  <Image
                    style={styles.icon}
                    source={image}
                    contentFit="contain"
                  />
                  {unreadCount > 0 && (
                    <Badge
                      value={unreadCount}
                      containerStyle={styles.badgeContainer}
                      badgeStyle={styles.badge}
                    />
                  )}
                </View>
                <Text
                  style={[
                    styles.label,
                    {
                      color: focused ? theme.colors.black : 'grey',
                      fontWeight: focused ? 'bold' : 'normal',
                    },
                  ]}
                  numberOfLines={1}
                >
                  {label}
                </Text>
              </View>
            ),
          }
        }}
      >
        {orderTabs(
          user?.permissions?.showGroupedConversations,
          chatConfig?.showSentMessages,
          chatConfig?.allowMarkMessages
        )}
      </Tab.Navigator>
    </SafeAreaProvider>
  )
}

function orderTabs(
  showGroupedConversations: boolean | undefined,
  showSentMessages: boolean | undefined,
  allowMarkMessages: boolean | undefined
) {
  const tabs: Record<string, React.JSX.Element> = {
    dm: <Tab.Screen key="dm" component={ConversationsScreen} name="dm" />,
    groups: (
      <Tab.Screen key="groups" component={ConversationsScreen} name="groups" />
    ),
    announcements: (
      <Tab.Screen
        key="announcements"
        component={ConversationsScreen}
        name="announcements"
      />
    ),
    marked: (
      <Tab.Screen key="marked" component={ConversationsScreen} name="marked" />
    ),
    sent: <Tab.Screen key="sent" component={ConversationsScreen} name="sent" />,
    allConversations: (
      <Tab.Screen
        key="allConversations"
        component={ConversationsScreen}
        name="allConversations"
        options={{ tabBarStyle: { display: 'none' } }}
      />
    ),
  }

  if (showGroupedConversations) {
    const groupedTabs = [tabs.dm, tabs.groups, tabs.announcements]
    if (allowMarkMessages) groupedTabs.push(tabs.marked)
    if (showSentMessages) groupedTabs.push(tabs.sent)
    return groupedTabs
  } else {
    return [tabs.allConversations]
  }
}

function mostUnreadSection(sections: ConversationsSection[]) {
  if (sections.length === 0) {
    return undefined
  }
  const maxUnread = sections.reduce((a, b) => (a.unread > b.unread ? a : b))
  if (maxUnread.unread === 0) {
    return undefined
  }
  const typeToTab: Record<string, keyof ConversationTabParamList> = {
    direct: 'dm',
    custom: 'groups',
    group: 'groups',
    announcements: 'announcements',
  }
  return typeToTab[maxUnread.type]
}

const useStyles = makeStyles(theme => ({
  icon: { width: 35, height: 35 },
  search: {
    height: 20,
    width: 32,
  },
  tab: {
    padding: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  badgeContainer: {
    position: 'absolute',
    top: -3,
    right: -1,
    padding: 2,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors?.white,
    borderRadius: 25,
  },
  badge: { borderWidth: 0, backgroundColor: theme.colors.primary },
  tabBarItemContainer: {
    alignItems: 'center',
    flex: 1,
    marginTop: 5,
  },
  label: {
    textAlign: 'center',
    fontSize: 12,
    overflow: 'visible',
    marginTop: 2,
  },
  tabBarIconLabelContainer: { flex: 1, alignItems: 'stretch' },
  searchBarContainer: {
    backgroundColor: 'transparent',
    borderTopWidth: 0,
    borderBottomWidth: 0,
    paddingHorizontal: 10,
    paddingVertical: 5,
    margin: 5,
  },
  searchBarInputContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey11 : theme.colors?.secondary,
    height: 36,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  searchBarText: {
    color: theme.colors.grey3,
    marginLeft: 8,
    fontSize: 16,
    flex: 1,
  },
}))
